import { cx } from '@linaria/core';
import { useRef, useEffect } from 'react';
import { pm2ConfigStore, usePM2DisplayContext, PM2DisplayProvider } from '@pm2/store';
import { useLocation } from '@provider/navigation';
import { useIsBeingPrinted } from '@provider/print';
import { resets } from '@ui/css';
import { isPlatformNodeJS, isPlatformSafari } from '@utils/common';
import { withSlots, useToggleState } from '@utils/react';
import { Caption } from './atoms/Caption.js';
import { ExpandCollapseButton } from './atoms/ExpandCollapseButton.js';
import { snippetLayout, boxLayout3, boxLayout2, boxLayout1 } from './design-tokens/box.js';
import { useBackgroundState } from './hooks/useBackgroundState.js';
import { useExpandCollapse } from './hooks/useExpandCollapse.js';
import { TextComponentSelector } from './index.selectors.js';
import { StickyAudioPlayer } from './molecule.sticky-audio-player/_sticky-audio-player.js';
import { AudioPlayerToggler } from './molecule.sticky-audio-player/_toggler.js';
import { ribbonLabelCls, contentCls, containerCls, boxCls, headingsCls, spacingCls } from './text-component.style.js';
import { stringifyRibbonLabel } from './utils/stringify-ribbon-label.js';
import { jsxs, jsx } from 'react/jsx-runtime';

const TextComponent = withSlots(function TextComponent(props) {
  const location = useLocation();
  const isPrintContent = useIsBeingPrinted();
  const primaryLanguage = pm2ConfigStore.useSelector(state => state.config.settings.syntheticSpeech?.language) ?? "Danish";
  const {
    inLightbox,
    inLibrary
  } = usePM2DisplayContext();
  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const [audioToggled, audioActions] = useToggleState();
  const appliedLayout = (() => {
    switch (props.layout) {
      case "box-1":
        return boxLayout1;
      case "box-2":
        return boxLayout2;
      case "box-3":
        return boxLayout3;
      case "snippet":
        return snippetLayout;
      case "none":
      default:
        return undefined;
    }
  })();
  const forceExpanded = inLibrary || inLightbox || isPrintContent;
  const {
    hasSolidBackground,
    hasSolidBorder
  } = useBackgroundState(appliedLayout);
  const {
    canExpand,
    isExpanded,
    isTransitioning,
    collapse,
    expand
  } = useExpandCollapse(props.collapsible && !forceExpanded, {
    wrapperRef,
    ref: contentRef
  }, {
    scrambled: props.scrambled,
    initiallyExpanded: forceExpanded || !!props.initiallyExpanded
  }, {
    onCollapsed: props.onCollapsed,
    onExpanded: props.onExpanded
  });
  useEffect(() => {
    if (isPlatformNodeJS()) {
      return;
    }

    // Determine if the text component should be auto-expanded due to hash
    // changes.
    if (props.onHashChange?.() && !isExpanded) {
      expand();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);
  const ribbonLabel = stringifyRibbonLabel(props.ribbonId, primaryLanguage);
  return /*#__PURE__*/jsxs("section", {
    ref: wrapperRef,
    "data-component-type": TextComponentSelector,
    className: cx("gText", resets.rootCls, boxCls, headingsCls, spacingCls, props.collapsible && !forceExpanded && "collapsible", props.autoCenter && "autoCenter", props.layout && `layout--${props.layout}`, inLightbox && "inLightbox", isExpanded ? "expanded" : "collapsed", (hasSolidBackground || hasSolidBorder) && "solid"),
    children: [/*#__PURE__*/jsxs("div", {
      children: [/*#__PURE__*/jsxs("div", {
        ref: contentRef,
        "data-text-component-content": true,
        className: cx(containerCls, props.scrambled && "scrambled", isTransitioning && "transitioning", isExpanded ? "expanded" : "collapsed", isPlatformSafari() && "isSafari"),
        children: [props.attachedAudioFile && !isPrintContent && /*#__PURE__*/jsx(AudioPlayerToggler, {
          toggled: audioToggled,
          onToggle: audioActions.toggle
        }), ribbonLabel !== undefined && /*#__PURE__*/jsx("span", {
          className: ribbonLabelCls,
          children: ribbonLabel
        }), props.displayCaption && props.caption && /*#__PURE__*/jsx(Caption, {
          caption: props.caption,
          scrambled: props.scrambled,
          includeContext: "in-context"
        }), props.slots.findOne("BetweenCaptionAndContent")?.children, /*#__PURE__*/jsx(PM2DisplayProvider, {
          isEmbedded: true,
          children: /*#__PURE__*/jsx("div", {
            className: contentCls,
            "data-text-content": true,
            children: props.children
          })
        })]
      }), canExpand && !props.scrambled && !inLightbox && !inLibrary && !isPrintContent && /*#__PURE__*/jsx(ExpandCollapseButton, {
        isExpanded: isExpanded,
        isTransitioning: isTransitioning,
        collapse: collapse,
        expand: expand
      })]
    }), props.attachedAudioFile && !isPrintContent && /*#__PURE__*/jsx(StickyAudioPlayer, {
      audioSrc: props.attachedAudioFile,
      toggled: audioToggled,
      onDeactivate: audioActions.disable
    })]
  });
});

export { TextComponent };
//# sourceMappingURL=text-component.js.map
